import { useCallback, useEffect, useMemo, useState } from 'react'
import { RefreshCw, Target } from 'lucide-react'
import { adaptEngineMiddles, fetchCrossbookEdge, getEdgeFeedProof } from '../lib/edgeData'
import { getBookLogo } from '../lib/logoMaps'
import './MiddlesWidget.css'

const REFRESH_MS = 30_000

function formatOdds(american) {
  if (!Number.isFinite(Number(american))) return '—'
  return Number(american) > 0 ? `+${american}` : String(american)
}

export default function MiddlesWidget() {
  const [rows, setRows] = useState([])
  const [proof, setProof] = useState(null)
  const [league, setLeague] = useState('ALL')
  const [minWindow, setMinWindow] = useState(1)
  const [maxCost, setMaxCost] = useState(10)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const refresh = useCallback(async () => {
    try {
      setLoading(true)
      const payload = await fetchCrossbookEdge()
      setRows(adaptEngineMiddles(payload))
      setProof(getEdgeFeedProof(payload))
      setError(null)
    } catch (fetchError) {
      setError(fetchError.message || 'Middle feed unavailable')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    refresh()
    const interval = setInterval(refresh, REFRESH_MS)
    return () => clearInterval(interval)
  }, [refresh])

  const leagues = useMemo(
    () => ['ALL', ...Array.from(new Set(rows.map((row) => row.league))).sort()],
    [rows]
  )
  const visibleRows = useMemo(() => rows
    .filter((row) => league === 'ALL' || row.league === league)
    .filter((row) => row.window >= minWindow && row.costPct <= maxCost)
    .sort((left, right) => {
      if (left.costPct !== right.costPct) return left.costPct - right.costPct
      return right.window - left.window
    }), [rows, league, minWindow, maxCost])

  const proofAgeMs = proof?.capturedAt ? Math.max(0, Date.now() - new Date(proof.capturedAt).getTime()) : null
  const proofIsStale = proofAgeMs == null || proofAgeMs > 5 * 60_000

  return (
    <div className="widget mid-widget">
      <div className="widget-header">
        <h3 className="widget-title">
          <Target size={16} />
          Middle Finder
          <span className="mid-count">{visibleRows.length} windows</span>
        </h3>
        <button className="widget-refresh-btn" onClick={refresh} title="Refresh">
          <RefreshCw size={14} className={loading ? 'mid-spinner' : ''} />
        </button>
      </div>

      {proof && (
        <div className={`mid-source-proof ${proofIsStale ? 'is-stale' : 'is-live'}`}>
          <span>Cross-book edge engine</span>
          <span>{proof.books} books</span>
          <span>{proof.markets} married markets</span>
          <span>Captured {new Date(proof.capturedAt).toLocaleString()}</span>
          <span>{Math.round(proofAgeMs / 60_000)}m old</span>
        </div>
      )}

      <div className="mid-filters">
        <label>
          League
          <select value={league} onChange={(event) => setLeague(event.target.value)}>
            {leagues.map((entry) => <option key={entry} value={entry}>{entry === 'ALL' ? 'All Sports' : entry}</option>)}
          </select>
        </label>
        <label>
          Min window
          <input type="number" min="0.5" step="0.5" value={minWindow} onChange={(event) => setMinWindow(Number(event.target.value) || 0)} />
        </label>
        <label>
          Max cost %
          <input type="number" min="0" step="0.25" value={maxCost} onChange={(event) => setMaxCost(Number(event.target.value) || 0)} />
        </label>
      </div>

      {error && <div className="mid-error">{error}</div>}
      {loading && rows.length === 0 ? (
        <div className="mid-empty">Loading validated middle windows...</div>
      ) : visibleRows.length === 0 ? (
        <div className="mid-empty">No middle windows match the current filters.</div>
      ) : (
        <div className="mid-list">
          {visibleRows.map((row) => (
            <article className="mid-card" key={row.id}>
              <div className="mid-card-heading">
                <div>
                  <span className="mid-league">{row.league}</span>
                  <strong>{row.event}</strong>
                  <span>{row.date} · {row.period} {row.betType}</span>
                </div>
                <div className="mid-window">
                  <strong>{row.window}</strong>
                  <span>point window</span>
                </div>
              </div>
              <div className="mid-legs">
                <div className="mid-leg">
                  <span>OVER {row.over.line}</span>
                  <strong>{formatOdds(row.over.american)}</strong>
                  <span className="mid-book">
                    {getBookLogo(row.over.book) && <img src={getBookLogo(row.over.book)} alt="" onError={(event) => { event.target.style.display = 'none' }} />}
                    {row.over.book}
                  </span>
                </div>
                <div className="mid-hit">
                  <span>Middle on</span>
                  <strong>{row.middleNumbers.join(', ') || 'any value inside window'}</strong>
                  <span>Entry cost {row.costPct.toFixed(2)}%</span>
                </div>
                <div className="mid-leg">
                  <span>UNDER {row.under.line}</span>
                  <strong>{formatOdds(row.under.american)}</strong>
                  <span className="mid-book">
                    {getBookLogo(row.under.book) && <img src={getBookLogo(row.under.book)} alt="" onError={(event) => { event.target.style.display = 'none' }} />}
                    {row.under.book}
                  </span>
                </div>
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  )
}
